'use client';

import { useEffect, useMemo, useState, type PropsWithChildren, type ReactNode } from 'react';
import isEmpty from 'lodash/isEmpty';
import { usePathname } from 'next/navigation';
import { UnavailablePage } from 'app/components/error-page/unavailable-page';
import { useAuth } from 'app/queries/auth/use-auth';
import { useClearSessionMutation } from 'app/queries/auth/use-clear-session-mutation';
import { useConfigSystem } from 'app/queries/config/use-config-system';
import { login } from 'app/lib/auth/login-redirect';
import { HomeLayout } from 'app/lib/features/layout/home-page-layout';
import { LoadingPage } from 'app/lib/auth/context/loading-page';
import { ConsentStatus, ConsentType } from 'app/types/api/user-v1';
import { TermsOfService } from 'app/components/free-trial/terms-of-service';
import { FreeTrial } from 'app/components/free-trial';
import { Homepage } from 'app/components/homepage';

export function AuthGate({ children }: PropsWithChildren): ReactNode {
  const { auth, isLoading, error, isLoadingError } = useAuth();
  const { isPublic } = useConfigSystem();

  const isAccountant = auth.accountant();
  const isAuthenticated = auth.authenticated();
  const consents = auth.consents();
  const tenants = auth.tenants(true);

  const pathname = usePathname();
  const isHomePage = pathname === '/';
  const isFreeTrialPage = pathname.startsWith('/start-free-trial');
  const isTenantpage = pathname.startsWith('/tenant');

  // Check terms of service consent for public systems
  const hasAcceptedTos = useMemo(() => {
    return consents?.some(({ type, status }) => {
      return type === ConsentType.TERMS_OF_SERVICE && status === ConsentStatus.ACCEPTED;
    });
  }, [consents]);

  // Latch a failed initial identity load. `isLoadingError` is only true once the
  // query has settled into error, so a re-armed or remounted query (e.g. a
  // retryOnMount refetch that resets to pending) would otherwise flip it back to
  // false and drop the gate onto an endless loading splash while the failure
  // persists. Cleared once the query settles successfully, whether authenticated
  // or a clean unauthenticated result (a recovered backend returning an empty
  // identity), so recovery still routes the user in rather than stranding them on
  // the error page; kept latched while pending so a re-arm does not flash loading.
  //
  // On latching, also drop any active session cookie (fire-and-forget, local
  // clear only): a session we cannot verify is not worth keeping, and clearing
  // it means the next reload starts from a clean login instead of retrying
  // against the same dead session.
  const { mutate: clearSession } = useClearSessionMutation();
  const [identityLoadFailed, setIdentityLoadFailed] = useState(false);
  useEffect(() => {
    if (isLoadingError) {
      if (!identityLoadFailed) {
        // Breadcrumb for debugging sustained identity failures (picked up by
        // Faro where enabled): which status latched the gate into its terminal
        // error state before the session clear fired.
        // eslint-disable-next-line no-console
        console.error(
          `[auth-gate] identity load failed (status ${error?.code ?? 'none, network error'}), clearing session`,
        );
        setIdentityLoadFailed(true);
        clearSession();
      }
    } else if (!isLoading) {
      // Query settled without error (authenticated, or a clean unauthenticated
      // result). Only pending states are left latched, so a re-arm does not flash
      // the loading splash while a genuine recovery clears the terminal state.
      setIdentityLoadFailed(false);
    }
  }, [isLoadingError, isLoading, identityLoadFailed, clearSession, error?.code]);

  // A transient/server failure verifying the session (5xx, network, 403) is not
  // a logout. Surface it instead of falling through to the login() redirect
  // below, which reloads the whole app and loops while the failure persists
  // (e.g. during a backend/deploy blip). A genuine 401 is resolved to an empty
  // identity upstream, so it does not reach here as an error. An error without
  // an HTTP status is a network-level failure, which on a private system is
  // the missing-VPN / blocked-local-network-access signature, so the page adds
  // that guidance.
  if (isLoadingError || identityLoadFailed) {
    return (
      <UnavailablePage error={error} showLocalNetworkHint={!isPublic && error?.code == null} />
    );
  }

  // Unauthenticated visitor on the home page: show the public homepage.
  if (isHomePage && !isLoading && !isAuthenticated) {
    return (
      <HomeLayout>
        <Homepage loading={isLoading} />
      </HomeLayout>
    );
  }

  if (!isAuthenticated || isLoading) {
    if (!isLoading) {
      login();
    }
    return <LoadingPage />;
  }

  if (!hasAcceptedTos && isPublic) {
    // Let the Next.js page render with correct route params (e.g. partner-referral type)
    if (isFreeTrialPage) {
      return children;
    }
    if (isEmpty(tenants) && !isAccountant && isTenantpage) {
      return <FreeTrial />;
    }
    return <TermsOfService />;
  }

  return children;
}
